import React, { useState, useEffect } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import {
  CalendarMonth,
  AccountBalanceWallet,
  ReceiptLong,
  EditCalendar,
  Map as MapIcon,
  Chat,
  Phone,
  QrCode2,
} from '@mui/icons-material';
import comms from './comms';
import { countTripsInSteps } from './Scheduling/schedulingUtils';
import Cashiering from './Dashboard/Cashiering/Cashiering';
import Marketplace from './Dashboard/Marketplace';
import TransactionsTabs from './Dashboard/TransactionsTabs';
import Scheduling from './Scheduling/Scheduling';
import GeolocationHandler from './Dispatch/LiveDispatch/GeolocationHandler';
import BottomSheet from './ui/BottomSheet';
import DriverQrSheet from './DriverQrSheet';
import ContactDispatchSheet from './ContactDispatchSheet';
import { resolveVehicleUnitId } from '../utils/resolveVehicleUnitId';
import { isMarketplaceAvailable } from '../utils/onDemandOffer';

const BASE_TILES = [
  { id: 'transactions', label: 'Transactions', icon: ReceiptLong, component: TransactionsTabs },
  { id: 'cash', label: 'Cashiering', icon: AccountBalanceWallet, component: Cashiering },
];

// Marketplace is available in Live dispatch AND itinerary; the fixed shuttle route
// types are the exclusion. See isMarketplaceAvailable().
const MARKETPLACE_TILE = {
  id: 'marketplace',
  label: 'Marketplace',
  icon: CalendarMonth,
  component: Marketplace,
};


// Scheduling component, surfaced under the "Availability" name
const AVAILABILITY_TILE = {
  id: 'availability',
  label: 'Availability',
  screenTitle: 'Availability',
  icon: EditCalendar,
  component: Scheduling,
};

function formatToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}/${m}/${day}`;
}

// Driver/Schedule returns EITHER schedule.itinerary OR trips[] — never both.
function countScheduleTrips(res) {
  const itinerary = res?.schedule?.itinerary;
  if (Array.isArray(itinerary) && itinerary.length > 0) {
    return countTripsInSteps(itinerary);
  }
  return (res?.trips || []).length;
}

function Dashboard({ navigateTo, userSettings, fleetSettings, googleMapsLoaded = false, googleApiKey = null, selectedVehicle = null, dispatchType = null, onOpenChat = null, onRegisterRefresh = null }) {
  const [activeView, setActiveView] = useState('main');
  const [todayTrips, setTodayTrips] = useState(null); // null = loading, number = loaded, undefined = error
  const [sheetOpen, setSheetOpen] = useState(false);
  const [qrSheetOpen, setQrSheetOpen] = useState(false);
  const [contactSheetOpen, setContactSheetOpen] = useState(false);
  const [vehicleStatus, setVehicleStatus] = useState('Clear'); // 'Clear' = Available, 'Busy' = on break
  const [center, setCenter] = useState(null);
  const [, setIsAccurate] = useState(false);
  const driverName = userSettings?.Name || userSettings?.name || '';

  // Scheduling tile appears when the fleet config enables route bidding or
  // driver scheduling. The Route Bidding guide (§5) ships bidding as a boolean
  // `routeBidding: true`; accept that, plus the legacy `bidding: true` and
  // `routeBidding.enabled` object forms. Driver scheduling rides
  // `weeklyPlanning.driverScheduling` (route self-scheduling spec §2).
  const biddingEnabled =
    fleetSettings?.bidding === true ||
    fleetSettings?.routeBidding === true ||
    fleetSettings?.routeBidding?.enabled === true;
  const schedulingEnabled = fleetSettings?.weeklyPlanning?.driverScheduling === true;
  const resolvedFleetCode = (fleetSettings?.unitId || fleetSettings?.fleetCode || localStorage.getItem('fleetCode') || '').toUpperCase();
  const isAcetFleet = resolvedFleetCode === 'ACET';
  const showAvailability = isAcetFleet || biddingEnabled || schedulingEnabled;
  const showMarketplace = isMarketplaceAvailable(dispatchType);
  const tiles = [
    ...(showMarketplace ? [MARKETPLACE_TILE] : []),
    ...BASE_TILES,
    ...(showAvailability ? [AVAILABILITY_TILE] : []),
  ];

  useEffect(() => {
    let cancelled = false;
    const driverId = userSettings?.Id || JSON.parse(localStorage.getItem('userSettings') || '{}')?.Id;
    if (!driverId) {
      setTodayTrips(undefined);
      return;
    }
    comms
      .getDriverSchedule(driverId, formatToday())
      .then((res) => {
        if (cancelled) return;
        setTodayTrips(res?.status ? countScheduleTrips(res) : undefined);
      })
      .catch(() => !cancelled && setTodayTrips(undefined));
    return () => {
      cancelled = true;
    };
  }, [userSettings]);

  // Never mount GoogleMap before the script exposes Map — prevents Maps v3
  // minified "pH" / undefined crashes (same guard WaypointMap.js uses).
  const mapsRuntimeReady =
    typeof window !== 'undefined' &&
    typeof window.google?.maps?.Map === 'function' &&
    !!window.google?.maps?.event;
  const canMountGoogleMap = !!googleApiKey && googleMapsLoaded && mapsRuntimeReady;

  const handleToggleStatus = () => {
    const next = vehicleStatus === 'Clear' ? 'Busy' : 'Clear';
    if (next === 'Busy') {
      comms.setBusy();
    } else {
      comms.setClear();
    }
    setVehicleStatus(next);
  };

  const openSafety = () => {
    window.dispatchEvent(new Event('driverapp:open-sos'));
  };

  const goToDispatch = () => {
    setSheetOpen(false);
    navigateTo('dispatch');
  };

  // Same gate Sidebar.js uses for its "Chat with Dispatch" item — itinerary
  // and shuttle routes have a real ChatDialog; other dispatch types don't,
  // so Messages falls back to the Dispatch screen (LiveDispatch has its own
  // in-map Messages component) rather than opening an empty/broken dialog.
  const canOpenChat = !!onOpenChat && (
    dispatchType === 'itinerary' ||
    dispatchType === 'shuttlebus-looped' ||
    dispatchType === 'shuttlebus-fixed'
  );

  const goToMessages = () => {
    setSheetOpen(false);
    if (canOpenChat) {
      onOpenChat();
    } else {
      navigateTo('dispatch');
    }
  };

  // ── Sub-page view ────────────────────────────────────────────────
  if (activeView !== 'main') {
    const tile = tiles.find((t) => t.id === activeView);
    if (tile) {
      const Component = tile.component;
      return (
        <div className="min-h-full bg-gray-100">
          <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 h-14 shadow-sm">
            <button
              onClick={() => setActiveView('main')}
              aria-label="Back"
              className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100"
            >
              <span className="text-2xl leading-none text-gray-800">&#8592;</span>
            </button>
            <h1 className="text-lg font-bold text-gray-900">{tile.screenTitle || tile.label}</h1>
          </div>
          <div className="mx-auto max-w-md px-4 py-4">
            <Component
              navigateTo={navigateTo}
              userSettings={userSettings}
              fleetSettings={fleetSettings}
              onRegisterRefresh={onRegisterRefresh}
            />
          </div>
        </div>
      );
    }
  }

  // ── Home view — map-first shell (01-driver-app-shell.md) ──────────
  return (
    <div className="relative" style={{ height: 'var(--app-content-height)', overflow: 'hidden' }}>
      {/* Map */}
      <div className="absolute inset-0 bg-gray-200">
        {canMountGoogleMap && center ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={15}
            options={{ disableDefaultUI: true, zoomControl: true }}
          >
            <Marker position={center} />
          </GoogleMap>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            {googleApiKey ? 'Locating your vehicle…' : 'Map unavailable'}
          </div>
        )}
        <GeolocationHandler onLocationUpdate={setCenter} setIsAccurate={setIsAccurate} />
      </div>

      {/* Status pill + Safety */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
        <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm">
          <span
            className={`h-2.5 w-2.5 rounded-full ${vehicleStatus === 'Clear' ? 'bg-emerald-500' : 'bg-amber-500'}`}
          />
          <span className="text-sm font-semibold text-gray-800">
            {vehicleStatus === 'Clear' ? 'Available' : 'On Break'}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={openSafety}
            className="rounded-full bg-red-600 px-3 py-2 text-xs font-semibold text-white shadow-sm"
          >
            Safety
          </button>
          <button
            onClick={handleToggleStatus}
            className="rounded-full bg-white px-3 py-2 text-xs font-semibold text-gray-800 shadow-sm"
          >
            {vehicleStatus === 'Clear' ? 'Go on Break' : 'Go Available'}
          </button>
        </div>
      </div>

      {/* Swipe-up handle / today summary */}
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="absolute bottom-0 left-0 right-0 rounded-t-3xl bg-white px-4 pt-2 text-left"
        style={{
          boxShadow: '0 -8px 30px rgba(15,23,42,0.15)',
          // pb-4 plus the iOS home-indicator inset, so the tap target and text
          // aren't sitting underneath the indicator on a notched iPhone.
          paddingBottom: 'calc(1rem + var(--app-safe-bottom))',
        }}
      >
        <div className="mx-auto mb-2 h-1.5 w-9 rounded-full bg-gray-300" />
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {driverName ? `Hi, ${driverName}` : 'Dashboard'}
          </span>
          <span className="text-sm font-semibold text-gray-900">
            {todayTrips === null ? (
              <span className="inline-block h-4 w-8 animate-pulse rounded bg-gray-200 align-middle" />
            ) : todayTrips === undefined ? (
              '—'
            ) : (
              `${todayTrips} ${todayTrips === 1 ? 'trip' : 'trips'} today`
            )}
          </span>
        </div>
        <div className="mt-1 text-xs font-semibold text-blue-700">Swipe up for menu ▲</div>
      </button>

      <BottomSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title="Dashboard">
        <div className="grid grid-cols-3 gap-3">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.id}
                onClick={() => {
                  setSheetOpen(false);
                  setActiveView(tile.id);
                }}
                className="flex flex-col items-center gap-1.5 rounded-2xl bg-gray-50 px-2 py-4 active:scale-95"
              >
                <Icon className="text-gray-700" style={{ fontSize: 26 }} />
                <span className="text-center text-xs font-semibold text-gray-800">{tile.label}</span>
              </button>
            );
          })}

          <button
            onClick={goToDispatch}
            className="flex flex-col items-center gap-1.5 rounded-2xl bg-gray-50 px-2 py-4 active:scale-95"
          >
            <MapIcon className="text-gray-700" style={{ fontSize: 26 }} />
            <span className="text-center text-xs font-semibold text-gray-800">Dispatch</span>
          </button>

          {/* Itinerary/shuttle routes open the same ChatDialog Sidebar's "Chat
              with Dispatch" uses. Other dispatch types (e.g. LiveDispatch)
              don't have that dialog wired up, so this falls back to the
              Dispatch map screen, which has its own in-map Messages view. */}
          <button
            onClick={goToMessages}
            className="flex flex-col items-center gap-1.5 rounded-2xl bg-gray-50 px-2 py-4 active:scale-95"
          >
            <Chat className="text-gray-700" style={{ fontSize: 26 }} />
            <span className="text-center text-xs font-semibold text-gray-800">
              Messages
              {!canOpenChat && (
                <span className="block text-[10px] font-normal text-gray-400">via Dispatch</span>
              )}
            </span>
          </button>

          <button
            onClick={() => {
              setSheetOpen(false);
              setContactSheetOpen(true);
            }}
            className="flex flex-col items-center gap-1.5 rounded-2xl bg-gray-50 px-2 py-4 active:scale-95"
          >
            <Phone className="text-gray-700" style={{ fontSize: 26 }} />
            <span className="text-center text-xs font-semibold text-gray-800">Contact Dispatch</span>
          </button>

          <button
            onClick={() => {
              setSheetOpen(false);
              setQrSheetOpen(true);
            }}
            className="flex flex-col items-center gap-1.5 rounded-2xl bg-gray-50 px-2 py-4 active:scale-95"
          >
            <QrCode2 className="text-gray-700" style={{ fontSize: 26 }} />
            <span className="text-center text-xs font-semibold text-gray-800">Driver QR</span>
          </button>
        </div>
      </BottomSheet>

      <ContactDispatchSheet
        open={contactSheetOpen}
        onClose={() => setContactSheetOpen(false)}
        canOpenChat={canOpenChat}
        onOpenChat={onOpenChat}
        driverName={driverName}
        driverPhone={userSettings?.phone || userSettings?.Phone}
        // Same resolver the socket uses to join the unit room, so chat can't end up
        // with a different (or empty) vehicle than the room it's talking to.
        vehicleNumber={resolveVehicleUnitId({ userSettings, selectedVehicle })}
        helpLinePhone={fleetSettings?.driverHelpLinePhone}
      />

      <DriverQrSheet
        open={qrSheetOpen}
        onClose={() => setQrSheetOpen(false)}
        driverId={userSettings?.Id || userSettings?.id}
        vehicleId={selectedVehicle?.id || selectedVehicle?.Id}
        // Fleet code drives the RiderApp host (https://<fleet>.riderapp.net/pay);
        // riderAppUrl is only an approved nonstandard-host override.
        fleetCode={resolvedFleetCode}
        riderAppUrl={fleetSettings?.riderAppUrl}
      />
    </div>
  );
}

export default Dashboard;
